import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { acquireAccessToken } from '@/auth/tokenProvider';
import type { ProblemDetails } from '@/types';

/**
 * Thrown for every non-2xx response. Wraps the backend's RFC 7807
 * problem+json body (docs/AGENT_PROMPT.md §5) so callers/UI can surface
 * `detail` to the user and branch on `status` (401/403/404/409/410/413/422...).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails;

  constructor(problem: ProblemDetails) {
    super(problem.detail || problem.title || 'Request failed');
    this.name = 'ApiError';
    this.status = problem.status;
    this.problem = problem;
  }
}

export class ForbiddenError extends ApiError {}
export class UnauthorizedError extends ApiError {}

function isProblemDetails(data: unknown): data is ProblemDetails {
  return typeof data === 'object' && data !== null && 'title' in data && 'status' in data;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  headers: {
    Accept: 'application/json',
  },
});

apiClient.interceptors.request.use(async (config) => {
  const token = await acquireAccessToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (!error.response) {
      // Network error / no connectivity / CORS failure - no problem+json body available.
      const problem: ProblemDetails = {
        type: 'about:blank',
        title: 'Network Error',
        status: 0,
        detail: error.message || 'Unable to reach the server. Check your connection and try again.',
      };
      return Promise.reject(new ApiError(problem));
    }

    const { status } = error.response;
    let data: unknown = error.response.data;
    if (data instanceof Blob) {
      const text = await data.text();
      if (data.type.includes('json')) {
        try {
          data = JSON.parse(text);
        } catch (cause) {
          if (!(cause instanceof SyntaxError)) throw cause;
          data = text;
        }
      } else {
        data = text;
      }
    }
    const problem: ProblemDetails = isProblemDetails(data)
      ? data
      : {
          type: 'about:blank',
          title: error.response.statusText || 'Request Failed',
          status,
          detail:
            (typeof data === 'string' && data) || error.message || 'An unexpected error occurred.',
        };

    if (status === 401) {
      return Promise.reject(new UnauthorizedError(problem));
    }
    if (status === 403) {
      return Promise.reject(new ForbiddenError(problem));
    }
    return Promise.reject(new ApiError(problem));
  },
);
